import type { ChildAppConfig } from '@shared/app-config'
import type { ProxyConfig } from '@shared/wippy-package/wippy-package'
import { clearAll, setAutoAccept } from './config-store'

const OVERLAY_TAG = 'wippy-dev-overlay'

const overlayStyles = /* css */ `
  :host {
    all: initial;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    color: #e0e0e0;
  }
  .fab {
    position: fixed;
    bottom: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #2d2d2d;
    border: 1px solid #555;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2147483647;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    transition: background 0.15s;
  }
  .fab:hover { background: #444; }
  @keyframes fab-pulse {
    0%, 100% { box-shadow: 0 2px 8px rgba(0,0,0,0.4); }
    50% { box-shadow: 0 0 0 8px rgba(37,99,235,0.3), 0 2px 8px rgba(0,0,0,0.4); }
  }
  .fab.waiting {
    animation: fab-pulse 1.5s ease-in-out infinite;
    background: #2563eb;
    border-color: #2563eb;
  }
  .fab.waiting:hover { background: #1d4ed8; }
  .speech-bubble {
    position: fixed;
    bottom: 24px;
    right: 64px;
    background: #2563eb;
    color: #fff;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    z-index: 2147483647;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: none;
  }
  .speech-bubble::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-left-color: #2563eb;
    border-right: none;
  }
  .speech-bubble.visible { display: block; }
  .panel {
    position: fixed;
    bottom: 68px;
    right: 16px;
    width: 420px;
    max-height: 80vh;
    background: #1e1e1e;
    border: 1px solid #555;
    border-radius: 8px;
    z-index: 2147483647;
    box-shadow: 0 4px 20px rgba(0,0,0,0.6);
    display: none;
    flex-direction: column;
    overflow: hidden;
  }
  .panel.open { display: flex; }
  .panel-header {
    padding: 10px 14px;
    background: #2d2d2d;
    font-weight: 600;
    font-size: 14px;
    border-bottom: 1px solid #444;
  }
  .panel-body {
    padding: 12px 14px;
    overflow-y: auto;
    flex: 1;
  }

  /* Monitor section */
  .monitor {
    background: #161616;
    border: 1px solid #333;
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 10px;
  }
  .monitor-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 0;
    font-size: 12px;
  }
  .monitor-label {
    color: #888;
    min-width: 50px;
    flex-shrink: 0;
  }
  .monitor-value {
    color: #ccc;
    font-family: 'Cascadia Code', 'Fira Code', monospace;
    word-break: break-all;
    flex: 1;
  }
  .monitor-actions {
    display: flex;
    gap: 6px;
    margin-top: 6px;
  }
  button.btn-sm {
    padding: 3px 10px;
    border-radius: 3px;
    border: 1px solid #555;
    background: #2d2d2d;
    color: #e0e0e0;
    cursor: pointer;
    font-size: 11px;
    transition: background 0.15s;
  }
  button.btn-sm:hover { background: #3d3d3d; }

  /* Config section */
  .config-toggle {
    cursor: pointer;
    user-select: none;
    color: #888;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 6px 0;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .config-toggle:hover { color: #bbb; }
  .config-toggle .arrow { transition: transform 0.15s; }
  .config-toggle .arrow.open { transform: rotate(90deg); }
  .config-section { display: none; }
  .config-section.open { display: block; }

  .section-label {
    font-size: 11px;
    text-transform: uppercase;
    color: #888;
    margin: 10px 0 6px;
    letter-spacing: 0.5px;
  }
  .section-label:first-child { margin-top: 0; }
  textarea {
    width: 100%;
    min-height: 180px;
    background: #111;
    color: #e0e0e0;
    border: 1px solid #444;
    border-radius: 4px;
    padding: 8px;
    font-family: 'Cascadia Code', 'Fira Code', monospace;
    font-size: 12px;
    resize: vertical;
    box-sizing: border-box;
  }
  textarea:focus { outline: none; border-color: #6a9ef5; }
  .checkbox-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 0;
  }
  .checkbox-row label {
    cursor: pointer;
    user-select: none;
  }
  .checkbox-row input[type="checkbox"] {
    accent-color: #6a9ef5;
    cursor: pointer;
  }
  .panel-footer {
    padding: 10px 14px;
    border-top: 1px solid #444;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
  button.btn {
    padding: 6px 16px;
    border-radius: 4px;
    border: 1px solid #555;
    background: #2d2d2d;
    color: #e0e0e0;
    cursor: pointer;
    font-size: 13px;
    transition: background 0.15s;
  }
  button.btn:hover { background: #3d3d3d; }
  button.btn-primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
  }
  button.btn-primary:hover { background: #1d4ed8; }
  button.btn-danger {
    background: #991b1b;
    border-color: #991b1b;
    color: #fff;
  }
  button.btn-danger:hover { background: #7f1d1d; }
  .error-msg {
    color: #f87171;
    font-size: 12px;
    margin-top: 4px;
    display: none;
  }
`

export interface DevOverlayHandle {
  element: HTMLElement
  destroy: () => void
  updatePath: (path: string) => void
  updateTitle: (title: string) => void
  updateSize: (width: number, height: number) => void
  setOnRefresh: (fn: () => void) => void
  notifyWaiting: () => void
}

export interface DevOverlayOptions {
  onAccept: (config: ChildAppConfig, proxyConfig: ProxyConfig) => void
  onRefresh: () => void
  initialConfig: ChildAppConfig
  initialProxyConfig: ProxyConfig
  autoAccept: boolean
}

export function createDevOverlay(options: DevOverlayOptions): DevOverlayHandle {
  const { onAccept, onRefresh, initialConfig, initialProxyConfig, autoAccept } = options

  // Define custom element if not already registered
  if (!customElements.get(OVERLAY_TAG)) {
    customElements.define(OVERLAY_TAG, class extends HTMLElement {
      constructor() {
        super()
        this.attachShadow({ mode: 'open' })
      }
    })
  }

  const el = document.createElement(OVERLAY_TAG)
  const shadow = el.shadowRoot!

  const cssFlags = initialProxyConfig.injections.css
  const injFlags = initialProxyConfig.injections

  shadow.innerHTML = `
    <style>${overlayStyles}</style>
    <div class="speech-bubble" id="speech-bubble">Accept config to continue loading.</div>
    <button class="fab" title="Wippy Dev Config"><svg xmlns="http://www.w3.org/2000/svg" viewBox="110 90 305 340" fill="none" width="24" height="24"><path fill="#FF8201" d="m236 99-1 9c0 12 3 25 12 34 4 4 9 7 15 9 8 4 17 9 22 16 6 8 7 23 20 23 6 0 10-4 9-10-1-9-7-17-12-23l-3-4 16 4c20 9 35 26 42 46l3 11 1-2c3-9 3-19-1-28l8 4c9 6 15 17 19 27a109 109 0 0 1 7 64l-2 8 2 13c1 5 4 9 6 13a51 51 0 0 1 5 12l1 3c4 24-13 47-30 61a176 176 0 0 1-127 36c-39-4-90-24-115-55-11-15-16-36-8-53 4-7 10-13 10-21-1-7-4-10-6-16-6-12-6-26-5-39 2-17 10-41 24-52l8-4-2 8 1 16 2 4 1-5 3-9c4-10 11-17 12-28l1-9-2-14c7 2 13 8 17 13 6 7 8 15 10 24 2 8 7 18 15 21h8c8-5 1-13-2-17-23-26-11-68 14-88l2-2Z"/><path fill="#FFB90B" d="m236 99-1 9c0 12 3 25 12 34 4 4 9 7 15 9 8 4 17 9 22 16 6 8 7 23 20 23 6 0 10-4 9-10-1-9-7-17-12-23l-3-4 16 4c20 9 35 26 42 46l3 11-2 3c-2 6-6 10-11 14-2 2-3 3-7 3l-13 3-2 1-5 3c-3 2-8 6-9 10l-2 2-5 9c-4 6-10 11-17 13-6 2-16 3-22 2l-10-4c-4-2-6-4-7-8-2-4-1-6-5-9-5-3-15-2-20-4v1-1c-2-4-7-9-11-11l-4-3-3-1c-8-3-15-3-23-3-10 1-23-13-24-21l1-5 3-9c4-10 11-17 12-28l1-9-2-14c7 2 13 8 17 13 6 7 8 15 10 24 2 8 7 18 15 21h8c8-5 1-13-2-17-23-26-11-68 14-88l2-2Z"/><path fill="#FE5800" d="m236 99-1 9c0 12 3 25 12 34 4 4 9 7 15 9 8 4 17 9 22 16 6 8 7 23 20 23 6 0 10-4 9-10-1-9-7-17-12-23l-3-4 16 4c20 9 35 26 42 46l3 11-2 3c-2 6-6 10-11 14-2 2-3 3-7 3 10-18 0-38-14-50 1 7 0 14-4 20-2 3-6 3-10 4-7 1-19-6-24-10-4-3-9-9-12-15l-4-9c-4-6-10-9-16-13l-12-8-10-14-3-7-3 5-2 8c-2 7-2 14-1 22 4 13 17 15 19 34 0 4 0 8-3 12-3 5-9 9-15 10-7 1-15 0-21-4-10-7-15-17-18-29l-2-10c-4 6-9 13-10 20-3 10-2 23 4 31l3 3c-10 1-23-13-24-21l1-5 3-9c4-10 11-17 12-28l1-9-2-14c7 2 13 8 17 13 6 7 8 15 10 24 2 8 7 18 15 21h8c8-5 1-13-2-17-23-26-11-68 14-88l2-2Z"/><path fill="#00A2E4" d="m230 233 6-1 14-2h34l17 2c4 1 21 4 23 6l-5 3c-3 2-8 6-9 10l-2 2-5 9c-4 6-10 11-17 13-6 2-16 3-22 2l-10-4c-4-2-6-4-7-8-2-4-1-6-5-9-5-3-15-2-20-4v1-1c-2-4-7-9-11-11l-4-3 15-3 8-2Z"/><path fill="#FEEE5D" d="M255 182c5 2 6 4 10 8 6 7 10 16 16 22 6 5 12 8 19 8 5-1 9-2 13-5v5c-1 5-4 8-8 10l-3 1-1 1-17-2h-34l-14 2-6 1-1-1c1-2 7-5 10-6l9-9c7-9 4-14 5-25l2-10Z"/><path fill="#2E478E" d="m359 184 8 4c9 6 15 17 19 27a109 109 0 0 1 7 64l-2 8 2 13c1 5 4 9 6 13a51 51 0 0 1 5 12l1 3c4 24-13 47-30 61a176 176 0 0 1-127 36c-39-4-90-24-115-55-11-15-16-36-8-53 4-7 10-13 10-21-1-7-4-10-6-16-6-12-6-26-5-39 2-17 10-41 24-52l8-4-2 8 1 16 2 4c1 8 14 22 24 21 8 0 15 0 23 3l3 1 4 3c4 2 9 7 11 11l-1 2c-6-6-11-10-20-13-12-4-20-2-31 4l-2 1-13 12-1 4-3 6-4 10-1 4-1 8c-1 10 0 19-7 26l-3 7c-4 10-2 20 2 29 13 26 48 42 74 51 15 5 35 10 50 11 26 1 54-7 77-18 21-10 46-26 54-49 3-10 3-22-3-31l-5-10c-1-9 0-18-2-26v-20c1-17-4-33-11-48 0 4-2 8-4 12-2 5-4 10-9 14l-3 4-9-3a36 36 0 0 0-28 9l-2 2c-1 0-6 3-6 1 1-4 6-8 9-10l5-3 2-1 13-3c4 0 5-1 7-3a38 38 0 0 0 13-17l1-2c3-9 3-19-1-28Z"/><path fill="#FE5800" d="m146 282-3-5a71 71 0 0 1 3-65l1 4 2 6c5 10 10 18 21 23l-2 1-13 12-1 4-3 6-4 10-1 4Z"/><path fill="#FEB509" d="M155 258c-5-8-8-17-8-27l2-9c5 10 10 18 21 23l-2 1-13 12-1 4-3 6 4-10Z"/><path fill="#0073D1" d="M310 251c0 2 5-1 6-1v1l-2 2-5 14-1 4c-2 12-7 22-18 29-7 5-18 6-27 4-13-3-23-13-28-24l-7-15-5-9-1-1-1-1 1-2v1-1c5 2 15 1 20 4 4 3 3 5 5 9 1 4 3 6 7 8l10 4c6 1 16 0 22-2 7-2 13-7 17-13l5-9 2-2Z"/><path fill="#FEEE5D" d="m316 250 2-2 8-5c6-3 14-5 20-4l9 3 4 2c4 2 7 4 9 7l3 4 2 2v1c-1 1-3 0-4-1l-8-5c-6-3-10-3-16-3-15 0-26 12-34 23v-5 2h-2v-2l5-14 2-2v-1Zm-146-5c11-6 19-8 31-4 9 3 14 7 20 13l1 1c-1 1 1 4 1 6 0 4 0 7 2 11l-5-6c-9-9-19-16-32-17-12-1-22 1-30 10l-3 4v-1h-1l1-4 13-12 2-1Z"/><path fill="#FEB509" d="M367 224h1c2 7 3 15 1 22l-1 5c-2-3-5-5-9-7l-4-2 3-4c5-4 7-9 9-14Zm2 33c1 1 3 2 4 1v-1l2 4v2c-2 0-5-3-6-4v-2Z"/><path fill="#0A183F" d="M208 329c-9 12-23 14-36 8-8-3-15-10-18-18-4-11-4-24 1-35 3-9 13-19 23-20l11 1 1 1 4 1c17 7 25 30 21 47l-1 2c0 4-4 10-6 13Z"/><path fill="#FEFEFE" d="M208 329c-9 12-23 14-36 8-8-3-15-10-18-18-4-11-4-24 1-35 3-9 13-19 23-20l11 1 1 1c-7 2-12 6-16 12-8 14-6 38 8 47 9 6 23 2 29-5l3-4c0 4-4 10-6 13Z"/><path fill="#0073D1" d="M208 329a28 28 0 0 1-39-3c-7-8-9-17-9-27 0-14 10-31 24-34h5l1 1c-7 2-12 6-16 12-8 14-6 38 8 47 9 6 23 2 29-5l3-4c0 4-4 10-6 13Z"/><path fill="#FEFEFE" d="M195 280a9 9 0 1 1 4 18 9 9 0 0 1-4-18Z"/><path fill="#0A183F" d="M346 265c4-1 9-1 14 1 19 10 24 38 16 56-5 14-21 22-35 16-4-1-8-5-11-8l-7-12-2-8c-3-16 5-38 21-44l4-1Z"/><path fill="#0073D1" d="M346 265c4-1 9-1 14 1 19 10 24 38 16 56-5 14-21 22-35 16-4-1-8-5-11-8l-7-12c1-1 1 0 2 1l8 7c5 2 10 2 15 1 24-8 20-57-6-60v-1l4-1Z"/><path fill="#FEFEFE" d="M346 265c4-1 9-1 14 1 19 10 24 38 16 56-5 14-21 22-35 16-4-1-8-5-11-8 5 3 10 5 16 5 15 0 26-15 27-30a40 40 0 0 0-1-13c-2-11-8-21-18-25l-8-2Z"/><path fill="#FEFEFE" d="M337 279a9 9 0 1 1 3 19 9 9 0 0 1-3-19Z"/><path fill="#0A183F" d="M168 345c6 0 9 4 14 7a121 121 0 0 0 53 23c10 0 17-1 26-3a151 151 0 0 1 30 0c10 2 19 3 28 0 13-4 25-11 35-20 5-4 10-11 17-5l1 5c-4 0-5-2-8 1l-10 10c-10 8-23 16-35 19-10 3-19 1-29 0l-12-1c-12 0-23 1-35 3-20 2-40-6-56-18l-9-7c-3-2-6-6-10-7h-6c-3 0-1-2 0-3 1-3 3-4 6-4Z"/><path fill="#0A183F" d="M241 344c7-1 19 11 10 13-5 0-11-2-13-8-1-2 0-4 3-5Zm65 0c6-1 7 3 4 7-2 3-5 5-9 6-4 0-7-1-4-6 1-3 5-6 9-7Z"/></svg></button>
    <div class="panel">
      <div class="panel-header">Wippy Dev</div>
      <div class="panel-body">
        <!-- Monitor section -->
        <div class="monitor">
          <div class="monitor-row">
            <span class="monitor-label">Path</span>
            <span class="monitor-value" id="mon-path">/</span>
          </div>
          <div class="monitor-row">
            <span class="monitor-label">Title</span>
            <span class="monitor-value" id="mon-title">(none)</span>
          </div>
          <div class="monitor-row">
            <span class="monitor-label">Size</span>
            <span class="monitor-value" id="mon-size">—</span>
          </div>
          <div class="monitor-actions">
            <button class="btn-sm" id="btn-refresh" title="Trigger @visibility(true) event">Trigger Refresh</button>
          </div>
        </div>

        <!-- Config section (collapsible) -->
        <div class="config-toggle" id="config-toggle">
          <span class="arrow" id="config-arrow">▸</span> Configuration
        </div>
        <div class="config-section" id="config-section">
          <div class="section-label">App Config (JSON)</div>
          <textarea id="config-json">${JSON.stringify(initialConfig, null, 2)}</textarea>
          <div class="error-msg" id="json-error"></div>

          <div class="section-label">Proxy Injections</div>
          <div class="checkbox-row"><input type="checkbox" id="css-theme" ${cssFlags.themeConfig ? 'checked' : ''}><label for="css-theme">CSS: Theme</label></div>
          <div class="checkbox-row"><input type="checkbox" id="css-iframe" ${cssFlags.iframe ? 'checked' : ''}><label for="css-iframe">CSS: Iframe</label></div>
          <div class="checkbox-row"><input type="checkbox" id="css-primevue" ${cssFlags.primevue ? 'checked' : ''}><label for="css-primevue">CSS: PrimeVue</label></div>
          <div class="checkbox-row"><input type="checkbox" id="css-markdown" ${cssFlags.markdown ? 'checked' : ''}><label for="css-markdown">CSS: Markdown</label></div>
          <div class="checkbox-row"><input type="checkbox" id="css-customCss" ${cssFlags.customCss ? 'checked' : ''}><label for="css-customCss">CSS: Custom CSS</label></div>
          <div class="checkbox-row"><input type="checkbox" id="css-customVars" ${cssFlags.customVariables ? 'checked' : ''}><label for="css-customVars">CSS: Custom Variables</label></div>
          <div class="checkbox-row"><input type="checkbox" id="inj-tailwind" ${injFlags.tailwindConfig ? 'checked' : ''}><label for="inj-tailwind">Tailwind Config</label></div>
          <div class="checkbox-row"><input type="checkbox" id="inj-resize" ${injFlags.resizeObserver ? 'checked' : ''}><label for="inj-resize">Resize Observer</label></div>
          <div class="checkbox-row"><input type="checkbox" id="inj-links" ${injFlags.preventLinkClicks ? 'checked' : ''}><label for="inj-links">Prevent Link Clicks</label></div>
          <div class="checkbox-row"><input type="checkbox" id="inj-icons" ${injFlags.iconifyIcons ? 'checked' : ''}><label for="inj-icons">Iconify Icons</label></div>
          <div class="checkbox-row"><input type="checkbox" id="inj-refresh" ${injFlags.refreshWhenVisible ? 'checked' : ''}><label for="inj-refresh">Refresh When Visible</label></div>
          <div class="checkbox-row"><input type="checkbox" id="inj-history" ${injFlags.historyPolyfill ? 'checked' : ''}><label for="inj-history">History Polyfill</label></div>
          <div class="checkbox-row"><input type="checkbox" id="inj-error-capture" ${injFlags.errorCapture ? 'checked' : ''}><label for="inj-error-capture">Error Capture</label></div>

          <div class="section-label">Options</div>
          <div class="checkbox-row"><input type="checkbox" id="auto-accept" ${autoAccept ? 'checked' : ''}><label for="auto-accept">Auto-accept on reload</label></div>
        </div>
      </div>
      <div class="panel-footer">
        <button class="btn btn-danger" id="btn-reset">Reset</button>
        <button class="btn btn-primary" id="btn-accept">Accept</button>
      </div>
    </div>
  `

  const fab = shadow.querySelector('.fab') as HTMLElement
  const panel = shadow.querySelector('.panel') as HTMLElement
  const textarea = shadow.getElementById('config-json') as HTMLTextAreaElement
  const jsonError = shadow.getElementById('json-error') as HTMLElement
  const monPath = shadow.getElementById('mon-path') as HTMLElement
  const monTitle = shadow.getElementById('mon-title') as HTMLElement
  const monSize = shadow.getElementById('mon-size') as HTMLElement
  const configToggle = shadow.getElementById('config-toggle') as HTMLElement
  const configArrow = shadow.getElementById('config-arrow') as HTMLElement
  const configSection = shadow.getElementById('config-section') as HTMLElement
  const speechBubble = shadow.getElementById('speech-bubble') as HTMLElement

  let currentRefreshFn = onRefresh

  fab.addEventListener('click', () => {
    panel.classList.toggle('open')
  })

  // Config section toggle
  configToggle.addEventListener('click', () => {
    configSection.classList.toggle('open')
    configArrow.classList.toggle('open')
  })

  // Refresh button — uses mutable callback so it can be wired post-creation
  shadow.getElementById('btn-refresh')!.addEventListener('click', () => {
    currentRefreshFn()
  })

  const $ = (id: string) => shadow.getElementById(id) as HTMLInputElement

  function readProxyConfig(): ProxyConfig {
    return {
      enabled: true,
      injections: {
        css: {
          themeConfig: $('css-theme').checked,
          iframe: $('css-iframe').checked,
          primevue: $('css-primevue').checked,
          markdown: $('css-markdown').checked,
          customCss: $('css-customCss').checked,
          customVariables: $('css-customVars').checked,
        },
        tailwindConfig: $('inj-tailwind').checked,
        resizeObserver: $('inj-resize').checked,
        preventLinkClicks: $('inj-links').checked,
        iconifyIcons: $('inj-icons').checked,
        refreshWhenVisible: $('inj-refresh').checked,
        historyPolyfill: $('inj-history').checked,
        errorCapture: $('inj-error-capture').checked,
      },
    }
  }

  shadow.getElementById('btn-accept')!.addEventListener('click', () => {
    let parsedConfig: ChildAppConfig
    try {
      parsedConfig = JSON.parse(textarea.value)
      jsonError.style.display = 'none'
    }
    catch (e) {
      jsonError.textContent = `Invalid JSON: ${(e as Error).message}`
      jsonError.style.display = 'block'
      return
    }

    const proxyConfig = readProxyConfig()
    setAutoAccept($('auto-accept').checked)
    fab.classList.remove('waiting')
    speechBubble.classList.remove('visible')
    panel.classList.remove('open')
    onAccept(parsedConfig, proxyConfig)
  })

  shadow.getElementById('btn-reset')!.addEventListener('click', () => {
    clearAll()
    window.location.reload()
  })

  document.body.appendChild(el)

  return {
    element: el,
    destroy: () => el.remove(),
    updatePath: (path: string) => {
      monPath.textContent = path
    },
    updateTitle: (title: string) => {
      monTitle.textContent = title || '(none)'
    },
    updateSize: (width: number, height: number) => {
      monSize.textContent = `${Math.round(width)} × ${Math.round(height)}`
    },
    setOnRefresh: (fn: () => void) => {
      currentRefreshFn = fn
    },
    notifyWaiting: () => {
      fab.classList.add('waiting')
      speechBubble.classList.add('visible')
    },
  }
}
